import React, { useState, useRef, useCallback } from 'react';
import { ChevronDown, X } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import type { TaxonomyEntry } from '../../types';

interface CategoryComboboxProps {
  partNumber: string;
  value: string | null;
  taxonomy: TaxonomyEntry[];
  onChange: (value: string | null) => void;
  /** null means no pre-fill (parse failed); empty taxonomy = loading */
  placeholder?: string;
}

/**
 * Category picker for one validation row.
 *
 * The list is rendered through a Radix Popover portal rather than as an
 * absolutely-positioned child. That is not cosmetic: the grid's table wrapper
 * is `overflow-x-auto`, and CSS computes a `visible` axis to `auto` when its
 * partner is `auto`/`scroll` — so the wrapper clips VERTICALLY too, and rows
 * near the bottom of the page lost most of the ~250px dropdown. No z-index can
 * escape that, because clipping is not stacking.
 *
 * The Popover also brings the two behaviours that were missing: it flips above
 * the trigger when there is no room below (`avoidCollisions`, on by default),
 * and it shifts to stay on screen (`collisionPadding`). The list additionally
 * caps its height to the space actually available, so a tight fit shrinks the
 * list instead of overflowing it.
 *
 * Decision: ADR-056 — see docs/decisions/ADR-056-row-dropdowns-escape-the-grid-clip.md
 */
export const CategoryCombobox: React.FC<CategoryComboboxProps> = ({
  partNumber,
  value,
  taxonomy,
  onChange,
  placeholder = 'Select category...',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedEntry = taxonomy.find((t) => t.id === value) ?? null;

  const filtered = query
    ? taxonomy.filter((t) => t.name.toLowerCase().includes(query.toLowerCase()))
    : taxonomy;

  // Radix owns open/close (trigger click, outside click, Escape); this only
  // resets the per-open search state.
  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    setQuery('');
    setHighlightedIndex(0);
  }, []);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  const selectEntry = useCallback(
    (entry: TaxonomyEntry | null) => {
      onChange(entry ? entry.id : null);
      closeDropdown();
    },
    [onChange, closeDropdown],
  );

  const clearValue = useCallback(
    (e: React.MouseEvent) => {
      // Keep the click off the trigger, or clearing would also open the list.
      e.stopPropagation();
      // Empty string = explicitly excluded
      onChange('');
      closeDropdown();
    },
    [onChange, closeDropdown],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlightedIndex]) {
        selectEntry(filtered[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      closeDropdown();
    }
  };

  // Displayed label in the trigger
  const displayLabel = value === ''
    ? '— Excluded —'
    : selectedEntry?.name ?? placeholder;

  const isExcluded = value === '';

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <div data-testid={`category-combobox-${partNumber}`}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={`w-full flex items-center justify-between gap-1 px-2 py-1.5 text-sm rounded border transition-colors text-left ${
              isExcluded
                ? 'border-qc-error/30 bg-qc-error/10 text-qc-error'
                : 'border-border bg-bg-surface text-text-primary hover:border-interactive-primary'
            }`}
          >
            <span className="flex-1 truncate">{displayLabel}</span>
            <div className="flex items-center gap-0.5 shrink-0">
              {value !== null && value !== undefined && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={clearValue}
                  onKeyDown={(e) => e.key === 'Enter' && clearValue(e as unknown as React.MouseEvent)}
                  className="p-0.5 hover:text-qc-error rounded"
                  aria-label="Clear category (exclude part)"
                >
                  <X className="w-3 h-3" />
                </span>
              )}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
            </div>
          </button>
        </Popover.Trigger>
      </div>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          // Keeps the list clear of the viewport edges when it flips or shifts.
          collisionPadding={8}
          // Radix focuses the content by default; send it to the search box so
          // typing works immediately, as it did before the rewrite.
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
          className="z-50 w-64 bg-bg-surface border border-border rounded-lg shadow-lg overflow-hidden"
        >
          <div className="p-1.5 border-b border-border">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setHighlightedIndex(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search categories..."
              data-testid={`category-combobox-input-${partNumber}`}
              className="w-full px-2 py-1 text-sm rounded border border-border bg-bg-subtle text-text-primary focus:outline-none focus:ring-1 focus:ring-interactive-primary"
            />
          </div>
          <ul
            className="overflow-y-auto"
            role="listbox"
            // Shrink to the room actually available rather than overflow it.
            // 13rem matches the previous max-h-52.
            style={{ maxHeight: 'min(13rem, var(--radix-popover-content-available-height, 13rem))' }}
          >
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-text-tertiary italic">No categories match</li>
            )}
            {filtered.map((entry, idx) => (
              <li
                key={entry.id}
                role="option"
                aria-selected={entry.id === value}
                onClick={() => selectEntry(entry)}
                className={`px-3 py-1.5 text-sm cursor-pointer ${
                  idx === highlightedIndex
                    ? 'bg-interactive-primary text-text-inverse'
                    : entry.id === value
                    ? 'bg-interactive-primary/10 text-interactive-primary'
                    : 'text-text-primary hover:bg-bg-subtle'
                }`}
              >
                {entry.name}
              </li>
            ))}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
